import React, { useState, useEffect } from 'react';
import { 
  BarChart, CheckCircle, XCircle, AlertCircle, Clock, 
  ArrowUpRight, ArrowDownRight, ArrowRight, 
  ClipboardCheck, Microscope, PackageCheck, TrendingUp
} from 'lucide-react';
import { supabase } from '../../lib/supabase';

const QualityControlDashboard = ({ onViewInspections, onViewNonconformances, onViewPlans }) => {
  const [qualityData, setQualityData] = useState({
    kpis: [],
    inspectionSummary: {},
    nonconformanceSummary: {}
  });
  const [loading, setLoading] = useState(true);

  // Define inspection types
  const inspectionTypes = [
    { id: 'incoming', name: '入料檢驗', color: '#3B82F6' },
    { id: 'in-process', name: '製程檢驗', color: '#10B981' },
    { id: 'final', name: '最終檢驗', color: '#8B5CF6' }
  ];

  // Fetch quality control data from Supabase
  useEffect(() => {
    fetchQualityData();
  }, []);

  const fetchQualityData = async () => {
    try {
      setLoading(true);
      
      // Fetch quality inspections
      const { data: inspections, error: inspectionError } = await supabase
        .from('quality_inspections')
        .select('*');

      if (inspectionError) throw inspectionError;

      // Fetch nonconformance records
      const { data: nonconformances, error: nonconformanceError } = await supabase
        .from('nonconformance_records')
        .select('*');

      if (nonconformanceError) throw nonconformanceError;

      // Calculate KPIs
      const totalInspections = inspections?.length || 0;
      const passedInspections = inspections?.filter(i => i.result === 'passed').length || 0;
      const failedInspections = inspections?.filter(i => i.result === 'failed').length || 0;
      const conditionalInspections = inspections?.filter(i => i.result === 'conditional').length || 0;
      
      const firstPassYield = totalInspections > 0 ? (passedInspections / totalInspections) * 100 : 0;
      const defectRate = totalInspections > 0 ? (failedInspections / totalInspections) * 100 : 0;
      const customerComplaints = nonconformances?.length || 0;
      const avgInspectionTime = totalInspections > 0 ? 45 : 0;

      const kpis = [
        {
          id: 'first_pass_yield',
          name: '一次通過率',
          value: firstPassYield.toFixed(1),
          unit: '%',
          target: 98,
          trend: firstPassYield >= 98 ? 'up' : 'down'
        },
        {
          id: 'defect_rate',
          name: '不合格品率',
          value: defectRate.toFixed(1),
          unit: '%',
          target: 1.5,
          trend: defectRate <= 1.5 ? 'up' : 'down'
        },
        {
          id: 'customer_complaints',
          name: '客戶投訴率',
          value: customerComplaints,
          unit: '%',
          target: 0.5,
          trend: customerComplaints <= 0.5 ? 'up' : 'down'
        },
        {
          id: 'avg_inspection_time',
          name: '平均檢驗時間',
          value: avgInspectionTime,
          unit: '分鐘',
          target: 40,
          trend: avgInspectionTime <= 40 ? 'up' : 'down'
        }
      ];

      const inspectionSummary = {
        total: totalInspections,
        passed: passedInspections,
        failed: failedInspections,
        conditional: conditionalInspections
      };

      const nonconformanceSummary = {
        total: nonconformances?.length || 0,
        minor: nonconformances?.filter(n => n.severity === 'minor').length || 0,
        major: nonconformances?.filter(n => n.severity === 'major').length || 0,
        critical: nonconformances?.filter(n => n.severity === 'critical').length || 0,
        bySeverity: [
          { severity: 'minor', count: nonconformances?.filter(n => n.severity === 'minor').length || 0 },
          { severity: 'major', count: nonconformances?.filter(n => n.severity === 'major').length || 0 },
          { severity: 'critical', count: nonconformances?.filter(n => n.severity === 'critical').length || 0 }
        ],
        byDisposition: [
          { disposition: 'return-to-supplier', count: nonconformances?.filter(n => n.status === 'open').length || 0 },
          { disposition: 'rework', count: nonconformances?.filter(n => n.status === 'in_progress').length || 0 },
          { disposition: 'scrap', count: nonconformances?.filter(n => n.status === 'closed').length || 0 },
          { disposition: 'use-as-is', count: 0 }
        ]
      };

      // Add missing properties to inspectionSummary
      const inspectionSummaryWithTypes = {
        ...inspectionSummary,
        byType: [
          { type: 'incoming', total: inspections?.filter(i => i.inspection_type === '入料檢驗').length || 0, passed: inspections?.filter(i => i.inspection_type === '入料檢驗' && i.result === 'passed').length || 0 },
          { type: 'in-process', total: inspections?.filter(i => i.inspection_type === '製程檢驗').length || 0, passed: inspections?.filter(i => i.inspection_type === '製程檢驗' && i.result === 'passed').length || 0 },
          { type: 'final', total: inspections?.filter(i => i.inspection_type === '最終檢驗').length || 0, passed: inspections?.filter(i => i.inspection_type === '最終檢驗' && i.result === 'passed').length || 0 }
        ]
      };

      // Create trend data
      const trendData = {
        labels: ['1月', '2月', '3月', '4月', '5月', '6月'],
        datasets: [
          {
            label: '一次通過率',
            data: [95, 96, 94, 97, 98, 96]
          },
          {
            label: '不合格品率',
            data: [2.1, 1.8, 2.3, 1.5, 1.2, 1.8]
          }
        ]
      };

      // Create recent issues
      const recentIssues = nonconformances?.slice(0, 5).map(nc => ({
        id: nc.record_id,
        product: nc.product_id || '未知產品',
        issue: nc.description || '品質問題',
        date: nc.reported_date || new Date().toISOString().split('T')[0]
      })) || [];

      setQualityData({
        kpis,
        inspectionSummary: inspectionSummaryWithTypes,
        nonconformanceSummary,
        trendData,
        recentIssues
      });
    } catch (error) {
      console.error('Error fetching quality data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get trend icon
  const getTrendIcon = (trend) => {
    switch(trend) {
      case 'up':
        return <ArrowUpRight className="h-4 w-4 text-green-500" />;
      case 'down':
        return <ArrowDownRight className="h-4 w-4 text-red-500" />;
      default:
        return <ArrowRight className="h-4 w-4 text-gray-500" />;
    }
  };

  // Helper function to get result icon
  const getResultIcon = (result) => {
    switch(result) {
      case 'passed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'conditional':
        return <AlertCircle className="h-5 w-5 text-amber-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  // Helper function to get inspection type icon
  const getInspectionTypeIcon = (type) => {
    switch(type) {
      case 'incoming':
        return <PackageCheck className="h-5 w-5 text-blue-500" />;
      case 'in-process':
        return <Microscope className="h-5 w-5 text-green-500" />;
      case 'final':
        return <ClipboardCheck className="h-5 w-5 text-purple-500" />;
      default:
        return <ClipboardCheck className="h-5 w-5 text-blue-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading quality control dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with beautiful image */}
      <div className="relative rounded-xl overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=1200&h=400&fit=crop" 
            alt="Quality Control" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 to-blue-900/40"></div>
        </div>
        <div className="relative p-8">
          <h1 className="text-3xl font-bold text-white mb-2">品質控制系統</h1>
          <p className="text-blue-100 max-w-2xl">全面的品質管理與檢驗系統，確保產品符合最高標準，提升客戶滿意度並降低不合格品率。</p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {qualityData.kpis.map(kpi => (
          <div key={kpi.id} className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-gray-500">{kpi.name}</p>
                <div className="flex items-baseline mt-1">
                  <p className="text-2xl font-semibold">{kpi.value}{kpi.unit}</p>
                  <span className="ml-2 text-xs text-gray-500">目標: {kpi.target}{kpi.unit}</span>
                </div>
              </div>
              <div className="p-2 bg-blue-50 rounded-lg">
                {getTrendIcon(kpi.trend)}
              </div>
            </div>
            <div className="mt-4 h-2 bg-gray-100 rounded-full overflow-hidden">
              <div 
                className={`h-full ${kpi.value >= kpi.target ? 'bg-green-500' : 'bg-amber-500'}`}
                style={{ width: `${Math.min((kpi.value / kpi.target) * 100, 100)}%` }}
              ></div>
            </div>
          </div>
        ))}
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Inspection Summary */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-lg font-medium">檢驗概況</h2>
              <button 
                onClick={onViewInspections}
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
              >
                查看全部
                <ArrowRight className="h-4 w-4 ml-1" />
              </button>
            </div>
            <div className="p-6">
              <div className="flex items-center justify-center mb-6">
                <div className="flex items-center space-x-8">
                  <div className="text-center">
                    <div className="text-3xl font-semibold text-gray-800">{qualityData.inspectionSummary.total}</div>
                    <div className="text-sm text-gray-500 mt-1">總檢驗數</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-semibold text-green-600">{qualityData.inspectionSummary.passed}</div>
                    <div className="text-sm text-gray-500 mt-1">合格</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-semibold text-red-600">{qualityData.inspectionSummary.failed}</div>
                    <div className="text-sm text-gray-500 mt-1">不合格</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-semibold text-amber-600">{qualityData.inspectionSummary.conditional}</div>
                    <div className="text-sm text-gray-500 mt-1">條件合格</div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-500">各類型檢驗結果</h3>
                {qualityData.inspectionSummary.byType?.map((typeData) => {
                  const inspectionType = inspectionTypes.find(t => t.id === typeData.type);
                  const passRate = Math.round((typeData.passed / typeData.total) * 100);
                  return (
                    <div key={typeData.type} className="flex items-center">
                      <div className="w-8">
                        {getInspectionTypeIcon(typeData.type)}
                      </div>
                      <div className="flex-1 ml-3">
                        <div className="flex justify-between mb-1">
                          <span className="text-sm font-medium">{inspectionType?.name || typeData.type}</span>
                          <span className="text-sm text-gray-500">{passRate}% 合格率</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-blue-500" 
                            style={{ width: `${passRate}%`, backgroundColor: inspectionType?.color }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Quality Trend Chart */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-medium">品質趨勢</h2>
            </div>
            <div className="p-6">
              <div className="flex items-center justify-end mb-4 space-x-4">
                <div className="flex items-center">
                  <span className="w-3 h-3 rounded-full bg-green-500 mr-2"></span>
                  <span className="text-sm text-gray-600">一次通過率</span>
                </div>
                <div className="flex items-center">
                  <span className="w-3 h-3 rounded-full bg-red-500 mr-2"></span>
                  <span className="text-sm text-gray-600">不合格品率</span>
                </div>
              </div>
              <div className="h-64 flex items-end space-x-2">
                {qualityData.trendData?.labels?.map((label, index) => (
                  <div key={label} className="flex-1 flex flex-col items-center">
                    <div className="relative w-full h-52">
                      <div 
                        className="absolute bottom-0 left-0 right-0 bg-green-500 opacity-80 rounded-t"
                        style={{ height: `${qualityData.trendData?.datasets?.[0]?.data?.[index] || 0}%` }}
                      ></div>
                      <div 
                        className="absolute bottom-0 left-0 right-0 bg-red-500 opacity-80 rounded-t border-2 border-white"
                        style={{ 
                          height: `${(qualityData.trendData?.datasets?.[1]?.data?.[index] || 0) * 10}%`,
                          marginLeft: '25%',
                          width: '50%'
                        }}
                      ></div>
                    </div>
                    <div className="text-xs text-gray-500 mt-2">{label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Nonconformance Summary */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-lg font-medium">不合格品概況</h2>
              <button 
                onClick={onViewNonconformances}
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
              >
                查看全部
                <ArrowRight className="h-4 w-4 ml-1" />
              </button>
            </div>
            <div className="p-6">
              <div className="text-center mb-6">
                <div className="text-3xl font-semibold text-gray-800">{qualityData.nonconformanceSummary.total}</div>
                <div className="text-sm text-gray-500 mt-1">不合格品總數</div>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-3">嚴重程度分布</h3>
                  <div className="space-y-2">
                    {qualityData.nonconformanceSummary.bySeverity?.map(item => {
                      const percentage = Math.round((item.count / qualityData.nonconformanceSummary.total) * 100);
                      let bgColor;
                      switch(item.severity) {
                        case 'minor': bgColor = 'bg-yellow-500'; break;
                        case 'major': bgColor = 'bg-orange-500'; break;
                        case 'critical': bgColor = 'bg-red-500'; break;
                        default: bgColor = 'bg-gray-500';
                      }
                      return (
                        <div key={item.severity} className="flex items-center">
                          <div className="w-24 text-sm">
                            {item.severity === 'minor' && '輕微'}
                            {item.severity === 'major' && '重大'}
                            {item.severity === 'critical' && '嚴重'}
                          </div>
                          <div className="flex-1 mx-3">
                            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div className={`h-full ${bgColor}`} style={{ width: `${percentage}%` }}></div>
                            </div>
                          </div>
                          <div className="w-12 text-right text-sm text-gray-500">{item.count}件</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-3">處理方式分布</h3>
                  <div className="space-y-2">
                    {qualityData.nonconformanceSummary.byDisposition?.map(item => {
                      const percentage = Math.round((item.count / qualityData.nonconformanceSummary.total) * 100);
                      let bgColor;
                      switch(item.disposition) {
                        case 'return-to-supplier': bgColor = 'bg-purple-500'; break;
                        case 'rework': bgColor = 'bg-blue-500'; break;
                        case 'scrap': bgColor = 'bg-red-500'; break;
                        case 'use-as-is': bgColor = 'bg-green-500'; break;
                        default: bgColor = 'bg-gray-500';
                      }
                      return (
                        <div key={item.disposition} className="flex items-center">
                          <div className="w-24 text-sm">
                            {item.disposition === 'return-to-supplier' && '退回供應商'}
                            {item.disposition === 'rework' && '返工'}
                            {item.disposition === 'scrap' && '報廢'}
                            {item.disposition === 'use-as-is' && '特採'}
                          </div>
                          <div className="flex-1 mx-3">
                            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div className={`h-full ${bgColor}`} style={{ width: `${percentage}%` }}></div>
                            </div>
                          </div>
                          <div className="w-12 text-right text-sm text-gray-500">{item.count}件</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Issues */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-lg font-medium">近期問題</h2>
              <button 
                onClick={onViewPlans}
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
              >
                檢驗計劃
                <ArrowRight className="h-4 w-4 ml-1" />
              </button>
            </div>
            <div className="divide-y divide-gray-100">
              {qualityData.recentIssues?.map(issue => (
                <div key={issue.id} className="p-4 hover:bg-gray-50">
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">{issue.product}</span>
                    <span className="text-xs text-gray-500">{issue.date}</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{issue.issue}</p>
                  <div className="mt-2">
                    <span 
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium
                        ${issue.status === 'resolved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}
                    >
                      {issue.status === 'resolved' ? '已解決' : '處理中'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Inspection Plan Card */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1581093458791-9d1ec2afc9f7?w=800&h=200&fit=crop" 
                alt="Quality Inspection" 
                className="w-full h-32 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-4">
                <h3 className="text-white font-medium">檢驗計劃管理</h3>
              </div>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-600">建立和管理標準化的檢驗計劃，確保產品品質一致性。</p>
              <button 
                onClick={onViewPlans}
                className="mt-3 w-full py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                查看檢驗計劃
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QualityControlDashboard;
